import {RefObject, useCallback, useEffect, useRef, useState} from 'react';
import {WebcamRefTypes} from '@chekinapp/ui';
import {Analytics, ANALYTIC_EVENTS} from '../../analytics';
import {useServerDocumentDetection} from './useServerDocumentDetection';

const AUTO_GIVEUP_MS = 12000;

type UseAutoDocumentCaptureProps = {
  videoRef: RefObject<WebcamRefTypes>;
  enabled: boolean;
  onCapture: () => void;
};

export function useAutoDocumentCapture({
  videoRef,
  enabled,
  onCapture,
}: UseAutoDocumentCaptureProps) {
  const {isModelLoading, isCaptureReady, guidance, startDetection} =
    useServerDocumentDetection({
      videoRef,
      shouldInitialize: enabled,
    });

  const detectionStartedAt = useRef<number | null>(null);
  const hasCaptured = useRef(false);
  const [manualFallback, setManualFallback] = useState(false);

  const start = useCallback(() => {
    if (!enabled) return;
    hasCaptured.current = false;
    if (!detectionStartedAt.current) detectionStartedAt.current = Date.now();
    startDetection();
  }, [enabled, startDetection]);

  useEffect(() => {
    if (!enabled || isModelLoading) return;
    startDetection();
  }, [enabled, isModelLoading, startDetection]);

  useEffect(() => {
    if (!enabled || isModelLoading || hasCaptured.current || manualFallback) return;
    if (!isCaptureReady) return;

    hasCaptured.current = true;
    Analytics.track(ANALYTIC_EVENTS.ivDocumentAutoCaptured, {
      mechanism: 'SERVER',
      ms_to_capture: detectionStartedAt.current
        ? Date.now() - detectionStartedAt.current
        : null,
    });
    onCapture();
  }, [enabled, isModelLoading, isCaptureReady, manualFallback, onCapture]);

  useEffect(() => {
    if (!enabled) return;

    const timeout = setTimeout(() => {
      if (!hasCaptured.current) setManualFallback(true);
    }, AUTO_GIVEUP_MS);
    return () => clearTimeout(timeout);
  }, [enabled]);

  return {
    isModelLoading,
    isCaptureReady,
    guidance,
    manualFallback,
    start,
  };
}
